import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { embedService } from '../../../services/embedService';

const CodeGenerator = ({ config, catalog }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('javascript');
  const [copied, setCopied] = useState(false);

  const generateJavaScriptCode = () => {
    return embedService?.generateEmbedScript(catalog?.id, config);
  };

  const generateIframeCode = () => {
    return embedService?.generateIframeEmbed(catalog?.id, config);
  };

  const handleCopy = async () => {
    const code = activeTab === 'javascript' ? generateJavaScriptCode() : generateIframeCode();
    const success = await embedService?.copyEmbedCode(code);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const tabs = [
    { id: 'javascript', label: 'JavaScript', icon: 'Code2' },
    { id: 'iframe', label: 'Iframe', icon: 'Frame' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center">
          <Icon name="Code" size={20} className="text-accent mr-2 md:mr-3" />
          <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
            {t('embedSettings.code.title')}
          </h2>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName={copied ? 'Check' : 'Copy'}
          iconPosition="left"
          onClick={handleCopy}
        >
          {copied ? t('embedSettings.code.copied') : t('embedSettings.code.copy')}
        </Button>
      </div>
      
      <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-md">
        <div className="flex items-start gap-2">
          <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
          <div className="text-sm text-foreground">
            <p className="font-medium mb-1">{t('embedSettings.code.readyTitle')}</p>
            <p className="text-muted-foreground">
              {t('embedSettings.code.readyDesc')}
            </p>
          </div>
        </div>
      </div>
      
      <div className="flex gap-2 mb-4 border-b border-border overflow-x-auto">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 md:py-3 text-sm md:text-base font-medium transition-base whitespace-nowrap flex-shrink-0 ${
              activeTab === tab?.id
                ? 'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            {tab?.label}
          </button>
        ))}
      </div>
      <div className="bg-muted rounded-lg p-3 md:p-4 overflow-x-auto">
        <pre className="text-xs md:text-sm text-foreground font-mono leading-relaxed whitespace-pre-wrap break-all">
          {activeTab === 'javascript' ? generateJavaScriptCode() : generateIframeCode()}
        </pre>
      </div>
      <div className="mt-4 md:mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <div className="p-3 md:p-4 bg-muted/50 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Zap" size={16} className="text-success" />
            <span className="text-xs md:text-sm font-medium text-foreground">
              {activeTab === 'javascript' ? t('embedSettings.code.jsRecommended') : t('embedSettings.code.iframeTitle')}
            </span>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">
            {activeTab === 'javascript' ? t('embedSettings.code.jsDesc') : t('embedSettings.code.iframeDesc')}
          </p>
        </div>

        <div className="p-3 md:p-4 bg-muted/50 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Shield" size={16} className="text-accent" />
            <span className="text-xs md:text-sm font-medium text-foreground">{t('embedSettings.code.securityTitle')}</span>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">
            {t('embedSettings.code.securityDesc')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CodeGenerator;